import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useRole,
  usePermissionsGrouped,
  useRolePermissions,
  useUpdateRolePermissions,
} from '../../services/httpServices/queries';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Info,
  Save,
  RefreshCw,
  Shield,
  Check,
  Search,
  X,
} from 'lucide-react';
import { formatModuleLabel, formatCategoryLabel, groupByCategory } from '../../lib/utils';
import { resolvePermissionRoutes } from '../../hooks/usePermissions';

interface PermissionItem {
  id: string;
  name: string;
  code: string;
}

function PermissionCheckbox({
  perm,
  isSelected,
  onToggle,
  onKeyDown,
}: {
  perm: PermissionItem;
  isSelected: boolean;
  onToggle: (id: string) => void;
  onKeyDown: (e: React.KeyboardEvent, id: string) => void;
}) {
  return (
    <div
      key={perm.id}
      role="checkbox"
      aria-checked={isSelected}
      tabIndex={0}
      onClick={() => onToggle(perm.id)}
      onKeyDown={(e) => onKeyDown(e, perm.id)}
      className={`group flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
        isSelected
          ? 'bg-primary/[0.04] border-primary/30 shadow-sm'
          : 'bg-surface border-border hover:border-primary/20 hover:bg-primary/[0.02]'
      }`}
    >
      <div
        className={`mt-0.5 shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
          isSelected
            ? 'bg-primary border-primary'
            : 'bg-transparent border-text-muted/30 group-hover:border-primary/40'
        }`}
      >
        <Check
          size={12}
          strokeWidth={3}
          className={`text-white transition-transform duration-200 ${
            isSelected ? 'scale-100' : 'scale-0'
          }`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text">{perm.name || 'Unknown'}</p>
        <p className="text-xs text-text-muted mt-0.5">{perm.code || perm.id}</p>
        {(() => {
          const routes = resolvePermissionRoutes(perm.code || '');
          if (routes.length === 0) return null;
          return (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {routes.map((route) => (
                <span
                  key={route}
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary"
                >
                  {route}
                </span>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export function RolePermissionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: roleData, isLoading: roleLoading, error: roleError } = useRole(id || '');
  const { data: permissionsData } = usePermissionsGrouped();
  const { data: rolePermissionsData } = useRolePermissions(id || '');
  const updateRolePermissions = useUpdateRolePermissions();

  const role = roleData?.data;
  const [search, setSearch] = useState('');
  const permissionsGrouped = useMemo(() => {
    const rawGrouped = permissionsData?.data || {};
    const result: Record<string, PermissionItem[]> = {};
    for (const [module, perms] of Object.entries(rawGrouped)) {
      if (!Array.isArray(perms)) continue;
      const validPerms = perms.filter(
        (p: any) => p && typeof p.id === 'string' && p.id.length > 0,
      );
      if (validPerms.length > 0) {
        result[module] = validPerms;
      }
    }
    return result;
  }, [permissionsData]);

  // Filter the catalog by module label, permission name/code and the app routes
  // each permission unlocks (e.g. search "/billing/cashbook").
  const filteredPermissionsGrouped = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return permissionsGrouped;
    const result: Record<string, PermissionItem[]> = {};
    for (const [module, perms] of Object.entries(permissionsGrouped)) {
      const matched = perms.filter((perm) => {
        const haystack = [
          perm.name,
          perm.code,
          module,
          formatModuleLabel(module),
          ...resolvePermissionRoutes(perm.code || ''),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      });
      if (matched.length > 0) result[module] = matched;
    }
    return result;
  }, [permissionsGrouped, search]);

  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const data = rolePermissionsData?.data;
    if (Array.isArray(data)) {
      setSelectedPermissionIds(
        data.filter((p: any) => p && typeof p.id === 'string').map((p: any) => p.id),
      );
    }
  }, [rolePermissionsData]);

  const allPermissionIds = useMemo(() => {
    return Object.values(permissionsGrouped)
      .flat()
      .map((p) => p.id);
  }, [permissionsGrouped]);

  const togglePermission = (permId: string) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permId) ? prev.filter((x) => x !== permId) : [...prev, permId],
    );
  };

  const handlePermissionKeyDown = useCallback(
    (e: React.KeyboardEvent, permId: string) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        togglePermission(permId);
      }
    },
    [],
  );

  const toggleModulePermissions = (modulePerms: PermissionItem[]) => {
    const ids = modulePerms.map((p) => p.id);
    if (ids.length === 0) return;
    const allSelected = ids.every((pid) => selectedPermissionIds.includes(pid));
    if (allSelected) {
      setSelectedPermissionIds((prev) => prev.filter((pid) => !ids.includes(pid)));
    } else {
      setSelectedPermissionIds((prev) => Array.from(new Set([...prev, ...ids])));
    }
  };

  const toggleAllPermissions = () => {
    if (selectedPermissionIds.length === allPermissionIds.length) {
      setSelectedPermissionIds([]);
    } else {
      setSelectedPermissionIds([...allPermissionIds]);
    }
  };

  const handleSave = async () => {
    if (!id) return;
    setSaveError(null);
    try {
      await updateRolePermissions.mutateAsync({ id, permissionIds: selectedPermissionIds });
      setShowSuccess(true);
    } catch (err: any) {
      setSaveError(
        err?.response?.data?.message || err?.message || 'Failed to save permissions. Please try again.',
      );
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    navigate('/roles');
  };

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (roleError || !role) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load role. Please try again.</span>
      </div>
    );
  }

  // Website-only roles tag blog authors; they must never carry dashboard
  // permissions, so there is nothing to configure here.
  if ((role as any).isWebsiteOnly) {
    return (
      <div className="w-full space-y-6 max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/roles')}
          className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm cursor-pointer"
        >
          <ArrowLeft size={18} />
          Back to Roles
        </button>
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-text">{role.name}</h1>
            <p className="text-sm text-text-muted">Website-only role</p>
          </div>
        </div>
        <div className="flex items-start gap-2 p-4 bg-info/10 border border-info/20 rounded-lg text-info text-sm">
          <Info size={18} className="mt-0.5 shrink-0" />
          <p>
            This role is used only to mark employees as selectable authors when writing website blog posts.
            Website-only roles cannot be assigned dashboard permissions, so there is nothing to configure here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Top Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/roles')}
          className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm cursor-pointer"
        >
          <ArrowLeft size={18} />
          Back to Roles
        </button>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-text">Manage Permissions</h1>
          <p className="text-sm text-text-muted">{role.name}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between bg-surface border border-border rounded-lg p-4">
        <span className="text-sm text-text-muted">
          {selectedPermissionIds.length} of {allPermissionIds.length} permissions selected
        </span>
        <button
          onClick={toggleAllPermissions}
          className="text-sm text-primary hover:text-primary-dark transition-colors flex items-center gap-1.5 font-medium"
        >
          <RefreshCw size={14} />
          {selectedPermissionIds.length === allPermissionIds.length ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
        <input
          type="text"
          placeholder="Search permissions by name, code, or page (e.g. /billing/cashbook)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-9 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Permissions by Module */}
      <div className="space-y-4">
        {Object.entries(filteredPermissionsGrouped).map(([module, perms]) => {
          const validPerms = Array.isArray(perms)
            ? perms.filter((p) => p && typeof p.id === 'string')
            : [];

          const moduleIds = validPerms.map((p) => p.id);
          const allModuleSelected =
            moduleIds.length > 0 && moduleIds.every((pid) => selectedPermissionIds.includes(pid));

          return (
            <div key={module} className="bg-surface border border-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-text uppercase tracking-wider">
                  {formatModuleLabel(module)}
                </h3>
                <button
                  onClick={() => toggleModulePermissions(validPerms)}
                  className="text-xs text-primary hover:text-primary-dark transition-colors flex items-center gap-1.5 font-medium"
                >
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center border transition-all duration-200 ${
                      allModuleSelected
                        ? 'bg-primary border-primary'
                        : 'bg-transparent border-primary/40'
                    }`}
                  >
                    <Check
                      size={10}
                      strokeWidth={3}
                      className={`text-white transition-transform duration-200 ${
                        allModuleSelected ? 'scale-100' : 'scale-0'
                      }`}
                    />
                  </div>
                  {allModuleSelected ? 'Deselect Module' : 'Select Module'}
                </button>
              </div>
              <div className="space-y-4">
                {groupByCategory(validPerms).map((section) => (
                  <div key={section.category ?? module}>
                    {section.category && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                          {formatCategoryLabel(section.category)}
                        </span>
                        <span className="flex-1 h-px bg-border" />
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {section.permissions.map((perm) => (
                        <PermissionCheckbox
                          key={perm.id}
                          perm={perm}
                          isSelected={selectedPermissionIds.includes(perm.id)}
                          onToggle={togglePermission}
                          onKeyDown={handlePermissionKeyDown}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {Object.keys(filteredPermissionsGrouped).length === 0 && (
          <div className="text-center py-12 text-text-muted bg-surface border border-border rounded-xl">
            {Object.keys(permissionsGrouped).length > 0
              ? 'No permissions match your search.'
              : 'No permissions found'}
          </div>
        )}
      </div>

      {/* Save Error */}
      {saveError && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span className="text-sm">{saveError}</span>
          <button
            onClick={() => setSaveError(null)}
            className="ml-auto p-1 hover:bg-danger/10 rounded transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4">
        <button
          onClick={() => navigate('/roles')}
          className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={updateRolePermissions.isPending}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
        >
          {updateRolePermissions.isPending ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Save size={16} />
          )}
          Save Permissions
        </button>
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-surface border border-border rounded-2xl shadow-xl max-w-sm w-full p-6 text-center space-y-4 animate-in fade-in zoom-in duration-200">
            <div className="mx-auto w-14 h-14 bg-success/10 rounded-full flex items-center justify-center">
              <Check size={28} className="text-success" strokeWidth={2.5} />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-text">Permissions Saved</h3>
              <p className="text-sm text-text-muted">
                Role permissions have been updated successfully.
              </p>
            </div>
            <button
              onClick={handleSuccessClose}
              className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
